import React, { useEffect, useMemo, useRef, useState } from 'react';
import { clamp, colorWithAlpha, mixColors, parseColorToRgb } from './liquidShared';
import './waterElbow.css';

const PIPE_PATH = 'M69 25 L69 79 Q69 101 93 101 L151 101';
const VERTICAL_DROP_PATH = 'M80 35 L80 81 Q80 92 90 96';
const VIEWBOX_WIDTH = 180;
const VIEWBOX_HEIGHT = 132;

const LEGACY_WATER_COLORS = ['#f0143c', '#F0143C', '#87D4FF'];
const LEGACY_SHELL_COLORS = ['rgba(240, 178, 188, 0.88)', 'rgba(168, 215, 228, 0.84)'];
const LEGACY_PIPE_GLASS_COLORS = ['rgba(142, 190, 214, 0.44)'];
const LEGACY_PIPE_BORDER_COLORS = ['rgba(156, 194, 216, 0.42)'];
const LEGACY_CLAMP_COLORS = ['rgba(118, 152, 153, 0.92)'];

const colorsEqual = (left, right) => {
  const rgbLeft = parseColorToRgb(left);
  const rgbRight = parseColorToRgb(right);
  if (!rgbLeft || !rgbRight) return String(left || '').trim() === String(right || '').trim();
  return rgbLeft.every((channel, index) => channel === rgbRight[index]);
};

const matchesAnyColor = (value, candidates) =>
  candidates.some((candidate) => colorsEqual(value, candidate));

const normalizeColor = (value, fallback, legacyValues = []) => {
  const normalized = String(value || '').trim();
  if (!normalized || matchesAnyColor(normalized, legacyValues)) {
    return fallback;
  }
  return normalized;
};

const hasStreamValue = (value, threshold = 0) => {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return false;
    const numericValue = Number(trimmed);
    if (Number.isFinite(numericValue)) {
      return Math.abs(numericValue) > Math.abs(Number(threshold) || 0);
    }
    return !['false', 'off', 'no', 'none', 'null', 'undefined'].includes(trimmed.toLowerCase());
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) && Math.abs(value) > Math.abs(Number(threshold) || 0);
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return true;
};

const ORIENTATION_TRANSFORMS = {
  'top-right': '',
  'top-left': `translate(${VIEWBOX_WIDTH} 0) scale(-1 1)`,
  'bottom-right': `translate(0 ${VIEWBOX_HEIGHT}) scale(1 -1)`,
  'bottom-left': `translate(${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}) scale(-1 -1)`,
};

const WaterElbow = ({
  title,
  transparentBackground,
  value,
  color,
  pipeColor,
  pipeBackgroundColor,
  pipeGlassColor,
  pipeBorderColor,
  clampColor,
  pipeHeight = 70,
  flowThreshold = 0,
  showLabel,
  onLabel,
  offLabel,
  direction = 'top-right',
  draggable,
  dims,
  embedded = false,
  preserveAspectRatio = 'xMidYMid meet',
  className = '',
  style,
}) => {
  const [isFlowing, setIsFlowing] = useState(() => hasStreamValue(value, flowThreshold));
  const instanceIdRef = useRef(`water-elbow-${Math.random().toString(36).slice(2, 10)}`);

  useEffect(() => {
    setIsFlowing(hasStreamValue(value, flowThreshold));
  }, [flowThreshold, value]);

  const widgetWidth = Number.isFinite(Number(dims?.width)) ? Number(dims.width) : 300;
  const widgetHeight = Number.isFinite(Number(dims?.height)) ? Number(dims.height) : 260;
  const defaultWaterColor = useMemo(
    () => mixColors('var(--icon-reset-password)', 'var(--part-color)', 0.28),
    []
  );
  const defaultShellColor = useMemo(
    () => mixColors('var(--header-color)', 'var(--part-color)', 0.24),
    []
  );
  const defaultPipeGlassColor = useMemo(
    () => mixColors('var(--part-color)', 'var(--cell-background)', 0.24),
    []
  );
  const defaultPipeBorderColor = useMemo(
    () => mixColors(defaultShellColor, 'var(--part-color)', 0.28),
    [defaultShellColor]
  );
  const defaultClampColor = useMemo(
    () => mixColors('var(--header-color)', 'var(--part-color)', 0.18),
    []
  );

  const stageWidth = useMemo(
    () => Math.round(Math.max(210, Math.min(widgetWidth * 0.98, widgetHeight * 1.9, 440))),
    [widgetHeight, widgetWidth]
  );
  const graphicWidth = useMemo(
    () => Math.round(Math.max(190, Math.min(stageWidth * 0.76, widgetHeight * 1.04, 320))),
    [stageWidth, widgetHeight]
  );

  const waterColor = useMemo(
    () => normalizeColor(color, defaultWaterColor, LEGACY_WATER_COLORS),
    [color, defaultWaterColor]
  );

  const shellColor = useMemo(() => {
    const normalized = normalizeColor(pipeColor, defaultShellColor, LEGACY_SHELL_COLORS);
    return colorsEqual(normalized, waterColor) ? defaultShellColor : normalized;
  }, [defaultShellColor, pipeColor, waterColor]);
  const defaultPipeBackgroundColor = useMemo(
    () => mixColors('var(--header-background)', shellColor, 0.14),
    [shellColor]
  );
  const resolvedPipeBackgroundColor = useMemo(
    () => normalizeColor(pipeBackgroundColor, defaultPipeBackgroundColor),
    [defaultPipeBackgroundColor, pipeBackgroundColor]
  );
  const resolvedPipeGlassColor = useMemo(
    () =>
      normalizeColor(
        pipeGlassColor,
        defaultPipeGlassColor,
        [...LEGACY_SHELL_COLORS, ...LEGACY_PIPE_GLASS_COLORS]
      ),
    [defaultPipeGlassColor, pipeGlassColor]
  );
  const resolvedPipeBorderColor = useMemo(
    () => normalizeColor(pipeBorderColor, defaultPipeBorderColor, LEGACY_PIPE_BORDER_COLORS),
    [defaultPipeBorderColor, pipeBorderColor]
  );
  const resolvedClampColor = useMemo(
    () => normalizeColor(clampColor, defaultClampColor, LEGACY_CLAMP_COLORS),
    [clampColor, defaultClampColor]
  );
  const interiorFluidColor = resolvedPipeBackgroundColor;

  const shellLight = useMemo(() => mixColors(shellColor, 'var(--part-color)', 0.35), [shellColor]);
  const shellDark = useMemo(() => mixColors(shellColor, 'var(--shadow-black-soft)', 0.28), [shellColor]);
  const cavityLight = useMemo(
    () => mixColors('var(--header-background)', interiorFluidColor, isFlowing ? 0.22 : 0.08),
    [interiorFluidColor, isFlowing]
  );
  const cavityDark = useMemo(
    () => mixColors('var(--shadow-black-soft)', interiorFluidColor, isFlowing ? 0.08 : 0.18),
    [interiorFluidColor, isFlowing]
  );
  const waterLight = useMemo(() => mixColors(waterColor, 'var(--part-color)', 0.52), [waterColor]);
  const waterMid = useMemo(() => mixColors(waterColor, 'var(--header-background)', 0.24), [waterColor]);
  const waterDark = useMemo(() => mixColors(waterColor, 'var(--shadow-black-soft)', 0.3), [waterColor]);
  const topCapColor = useMemo(
    () => mixColors(resolvedClampColor, shellColor, 0.42),
    [resolvedClampColor, shellColor]
  );
  const topCapInner = useMemo(() => mixColors(topCapColor, 'var(--part-color)', 0.28), [topCapColor]);
  const elbowClampColor = useMemo(
    () => mixColors(resolvedClampColor, topCapColor, 0.24),
    [resolvedClampColor, topCapColor]
  );
  const clampShadow = useMemo(
    () => mixColors(elbowClampColor, 'var(--shadow-black-soft)', 0.24),
    [elbowClampColor]
  );
  const currentHighlightColor = useMemo(
    () => colorWithAlpha(mixColors(waterLight, 'var(--part-color)', 0.18), 0.32),
    [waterLight]
  );
  const glassHighlightColor = useMemo(
    () => colorWithAlpha(mixColors('var(--part-color)', resolvedPipeGlassColor, 0.44), 0.34),
    [resolvedPipeGlassColor]
  );
  const glassFadeColor = useMemo(
    () => colorWithAlpha(mixColors('var(--part-color)', resolvedPipeGlassColor, 0.3), 0),
    [resolvedPipeGlassColor]
  );
  const elbowShadowColor = useMemo(
    () => colorWithAlpha('var(--shadow-black-soft)', 0.24),
    []
  );
  const verticalGlowColor = useMemo(
    () => colorWithAlpha(mixColors('var(--part-color)', waterLight, 0.22), 0.2),
    [waterLight]
  );
  const innerPipeStrokeColor = useMemo(
    () => colorWithAlpha(mixColors('var(--part-color)', resolvedPipeBorderColor, 0.28), 0.42),
    [resolvedPipeBorderColor]
  );
  const shellOutlineColor = useMemo(
    () => colorWithAlpha(mixColors(resolvedPipeBorderColor, 'var(--part-color)', 0.26), 0.28),
    [resolvedPipeBorderColor]
  );
  const clampHighlightColor = useMemo(
    () => colorWithAlpha(mixColors(elbowClampColor, 'var(--part-color)', 0.36), 0.52),
    [elbowClampColor]
  );
  const safePipeHeight = useMemo(
    () => clamp(Number(pipeHeight) || 70, 20, 108),
    [pipeHeight]
  );
  const elbowScale = useMemo(
    () => safePipeHeight / 70,
    [safePipeHeight]
  );
  const shellStrokeWidth = useMemo(
    () => clamp(safePipeHeight * 0.66, 16, 62),
    [safePipeHeight]
  );
  const shadowStrokeWidth = useMemo(
    () => clamp((safePipeHeight * 0.66) + 4, 20, 66),
    [safePipeHeight]
  );
  const cavityMaskStrokeWidth = useMemo(
    () => clamp(safePipeHeight * (30 / 70), 9, 42),
    [safePipeHeight]
  );
  const verticalCurrentStrokeWidth = useMemo(
    () => clamp(safePipeHeight * (8 / 70), 2.5, 12),
    [safePipeHeight]
  );
  const verticalGlowStrokeWidth = useMemo(
    () => clamp(safePipeHeight * (3 / 70), 1.2, 5.5),
    [safePipeHeight]
  );
  const innerCurrentStrokeWidth = useMemo(
    () => clamp(safePipeHeight * (9 / 70), 3, 13),
    [safePipeHeight]
  );
  const shellOutlineWidth = useMemo(
    () => clamp(safePipeHeight * (5 / 70), 1.5, 8),
    [safePipeHeight]
  );
  const topCapWidth = useMemo(
    () => clamp(52 * elbowScale, 42, 74),
    [elbowScale]
  );
  const topCapHeight = useMemo(
    () => clamp(15 * elbowScale, 11, 22),
    [elbowScale]
  );
  const topCapX = useMemo(
    () => 72 - (topCapWidth / 2),
    [topCapWidth]
  );
  const topCapY = useMemo(
    () => 21.5 - (topCapHeight / 2),
    [topCapHeight]
  );
  const topCapInnerWidth = useMemo(
    () => clamp(40 * elbowScale, 30, 58),
    [elbowScale]
  );
  const topCapInnerHeight = useMemo(
    () => clamp(10 * elbowScale, 7, 15),
    [elbowScale]
  );
  const topCapInnerX = useMemo(
    () => 73 - (topCapInnerWidth / 2),
    [topCapInnerWidth]
  );
  const topCapInnerY = useMemo(
    () => 21 - (topCapInnerHeight / 2),
    [topCapInnerHeight]
  );
  const clampRectWidth = useMemo(
    () => clamp(19 * elbowScale, 14, 28),
    [elbowScale]
  );
  const clampRectHeight = useMemo(
    () => clamp(46 * elbowScale, 34, 66),
    [elbowScale]
  );
  const clampRectX = useMemo(
    () => 116.5 - (clampRectWidth / 2),
    [clampRectWidth]
  );
  const clampRectY = useMemo(
    () => 97 - (clampRectHeight / 2),
    [clampRectHeight]
  );
  const clampHighlightWidthPx = useMemo(
    () => clamp(7 * elbowScale, 5, 11),
    [elbowScale]
  );
  const clampHighlightHeightPx = useMemo(
    () => clamp(31 * elbowScale, 22, 44),
    [elbowScale]
  );
  const clampHighlightX = useMemo(
    () => 116.5 - (clampHighlightWidthPx / 2),
    [clampHighlightWidthPx]
  );
  const clampHighlightY = useMemo(
    () => 95.5 - (clampHighlightHeightPx / 2),
    [clampHighlightHeightPx]
  );
  const glassStripWidth = useMemo(
    () => clamp(12 * elbowScale, 8, 18),
    [elbowScale]
  );
  const glassStripX = useMemo(
    () => 64 - (glassStripWidth / 2),
    [glassStripWidth]
  );
  const centerStripWidth = useMemo(
    () => clamp(12 * elbowScale, 8, 18),
    [elbowScale]
  );
  const centerStripX = useMemo(
    () => 80 - (centerStripWidth / 2),
    [centerStripWidth]
  );
  const edgeStripWidth = useMemo(
    () => clamp(8 * elbowScale, 5, 12),
    [elbowScale]
  );
  const edgeStripX = useMemo(
    () => 54 - (edgeStripWidth / 2),
    [edgeStripWidth]
  );

  const svgIdBase = useMemo(
    () =>
      String(title || 'elbow-widget')
        .replace(/[^A-Za-z0-9_-]/g, '_')
        .concat('-', instanceIdRef.current, '-', String(direction || 'top-right').replace(/[^A-Za-z0-9_-]/g, '_')),
    [direction, title]
  );

  const shellGradientId = `${svgIdBase}-shell`;
  const cavityGradientId = `${svgIdBase}-cavity`;
  const topWaterGradientId = `${svgIdBase}-top-water`;
  const poolWaterGradientId = `${svgIdBase}-pool-water`;
  const currentWaterGradientId = `${svgIdBase}-current-water`;
  const currentHighlightGradientId = `${svgIdBase}-current-highlight`;
  const verticalCurrentGradientId = `${svgIdBase}-vertical-current`;
  const glassGradientId = `${svgIdBase}-glass`;
  const clampGradientId = `${svgIdBase}-clamp`;
  const cavityMaskId = `${svgIdBase}-mask`;

  const orientationTransform =
    ORIENTATION_TRANSFORMS[direction] || ORIENTATION_TRANSFORMS['top-right'];

  const setGrabbing = (event, grabbing) => {
    if (event.target.closest('.widget-buttons-container')) return;
    if (embedded || !draggable) return;
    const element = event.target.closest('.elbow-widget-container');
    if (!element) return;
    element.classList.toggle('is-grabbing', Boolean(grabbing));
  };

  const stageStyle = useMemo(
    () => ({
      '--elbow-stage-width': `${stageWidth}px`,
      '--elbow-graphic-width': `${graphicWidth}px`,
      '--elbow-top-wave-duration': isFlowing ? '2.5s' : '4.1s',
      '--elbow-pool-wave-duration': isFlowing ? '1.6s' : '3.35s',
      '--elbow-pool-wave-secondary-duration': isFlowing ? '2.35s' : '4.8s',
      '--elbow-glass-duration': isFlowing ? '2.7s' : '5s',
      ...style,
    }),
    [graphicWidth, isFlowing, stageWidth, style]
  );

  const containerClass = [
    embedded ? '' : 'widget-container',
    'elbow-widget-container',
    embedded ? 'is-embedded' : '',
    (!embedded && draggable) ? 'is-draggable' : '',
    (!embedded && !draggable) ? 'not-draggable' : '',
    transparentBackground ? 'is-transparent' : '',
    isFlowing ? 'is-flowing' : 'is-idle',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const statusLabel = isFlowing ? (onLabel || 'Water Flowing') : (offLabel || 'No Flow');

  return (
    <div
      className={containerClass}
      onMouseDown={(event) => setGrabbing(event, true)}
      onMouseUp={(event) => setGrabbing(event, false)}
      onMouseLeave={(event) => setGrabbing(event, false)}
      draggable={embedded ? false : draggable}
      onDragStart={(event) => {
        if (embedded) return;
        const id = event.target.closest('.widget')?.id;
        if (!id) return;
        const rect = event.target.getBoundingClientRect();
        const offsetX = event.clientX - rect.x;
        const offsetY = event.clientY - rect.y;
        let dragData = `elbow;${id};${offsetX};${offsetY};`;
        dragData += id.startsWith('base') ? 'add' : 'move';
        event.dataTransfer.setData('text/plain', dragData);
      }}
      style={stageStyle}
    >
      {!embedded ? <h3 className="elbow-widget__title">{title}</h3> : null}

      <div className="elbow-graphic-container">
        <div className="elbow-stage">
          <svg
            className="elbow-stage__svg"
            viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
            preserveAspectRatio={preserveAspectRatio}
            aria-hidden="true"
          >
            <defs>
              <linearGradient id={shellGradientId} x1="54" y1="18" x2="138" y2="112" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor={colorWithAlpha(shellLight, 0.84)} />
                <stop offset="38%" stopColor={colorWithAlpha(shellColor, 0.78)} />
                <stop offset="100%" stopColor={colorWithAlpha(shellDark, 0.92)} />
              </linearGradient>

              <linearGradient id={cavityGradientId} x1="58" y1="22" x2="136" y2="112" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor={colorWithAlpha(cavityLight, 0.82)} />
                <stop offset="100%" stopColor={colorWithAlpha(cavityDark, 0.98)} />
              </linearGradient>

              <linearGradient id={topWaterGradientId} x1="58" y1="24" x2="86" y2="70" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor={colorWithAlpha(waterLight, 0.94)} />
                <stop offset="60%" stopColor={colorWithAlpha(waterMid, 0.92)} />
                <stop offset="100%" stopColor={colorWithAlpha(waterDark, 0.84)} />
              </linearGradient>

              <linearGradient id={poolWaterGradientId} x1="42" y1="84" x2="154" y2="116" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor={colorWithAlpha(waterMid, 0.92)} />
                <stop offset="60%" stopColor={colorWithAlpha(waterLight, 0.9)} />
                <stop offset="100%" stopColor={colorWithAlpha(waterDark, 0.84)} />
              </linearGradient>

              <linearGradient id={currentWaterGradientId} x1="44" y1="20" x2="146" y2="116" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor={colorWithAlpha(waterLight, 0.96)} />
                <stop offset="55%" stopColor={colorWithAlpha(waterMid, 0.94)} />
                <stop offset="100%" stopColor={colorWithAlpha(waterDark, 0.88)} />
              </linearGradient>

              <linearGradient id={currentHighlightGradientId} x1="46" y1="26" x2="92" y2="112" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor={currentHighlightColor} />
                <stop offset="100%" stopColor={colorWithAlpha(waterLight, 0.06)} />
              </linearGradient>

              <linearGradient id={verticalCurrentGradientId} x1="78" y1="34" x2="92" y2="98" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor={colorWithAlpha(waterLight, 0.94)} />
                <stop offset="60%" stopColor={colorWithAlpha(waterMid, 0.9)} />
                <stop offset="100%" stopColor={colorWithAlpha(waterDark, 0.82)} />
              </linearGradient>

              <linearGradient id={glassGradientId} x1="62" y1="18" x2="94" y2="118" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor={glassHighlightColor} />
                <stop offset="100%" stopColor={glassFadeColor} />
              </linearGradient>

              <linearGradient id={clampGradientId} x1="102" y1="74" x2="118" y2="114" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor={elbowClampColor} />
                <stop offset="100%" stopColor={clampShadow} />
              </linearGradient>

              <mask id={cavityMaskId}>
                <rect x="0" y="0" width={VIEWBOX_WIDTH} height={VIEWBOX_HEIGHT} fill="black" />
                <path
                  d={PIPE_PATH}
                  fill="none"
                  stroke="white"
                  strokeWidth={cavityMaskStrokeWidth}
                  strokeLinecap="butt"
                  strokeLinejoin="round"
                />
              </mask>
            </defs>

            <g transform={orientationTransform}>
              <path
                d={PIPE_PATH}
                fill="none"
                stroke={elbowShadowColor}
                strokeWidth={shadowStrokeWidth}
                strokeLinecap="butt"
                strokeLinejoin="round"
                transform="translate(2 3)"
              />

              <path
                d={PIPE_PATH}
                fill="none"
                stroke={`url(#${shellGradientId})`}
                strokeWidth={shellStrokeWidth}
                strokeLinecap="butt"
                strokeLinejoin="round"
              />

              <g mask={`url(#${cavityMaskId})`}>
                <rect x="40" y="18" width="120" height="102" fill={`url(#${cavityGradientId})`} />

                {isFlowing ? (
                  <g>
                    <path
                      className="elbow-water-top-surface"
                      d="M44 22 H88 V70 C 81 64, 73 60, 64 63 C 56 66, 49 72, 44 68 Z"
                      fill={`url(#${topWaterGradientId})`}
                    />
                    <path
                      className="elbow-water-top-sheen"
                      d="M50 24 H82 V63 C 76 58, 68 56, 61 59 C 55 61, 52 65, 50 62 Z"
                      fill={colorWithAlpha(waterLight, 0.26)}
                    />
                    <path
                      className="elbow-water-pool elbow-water-pool--primary"
                      d="M40 90 C 54 82, 68 90, 82 94 C 96 98, 110 88, 124 90 C 140 92, 152 88, 166 91 L166 126 L40 126 Z"
                      fill={`url(#${poolWaterGradientId})`}
                    />
                    <path
                      className="elbow-water-pool elbow-water-pool--secondary"
                      d="M32 97 C 46 90, 60 98, 74 101 C 88 104, 102 94, 116 97 C 132 100, 146 95, 168 97 L168 126 L32 126 Z"
                      fill={colorWithAlpha(waterLight, 0.38)}
                    />
                    <path
                      className="elbow-stage__vertical-current"
                      d={VERTICAL_DROP_PATH}
                      fill="none"
                      stroke={`url(#${verticalCurrentGradientId})`}
                      strokeWidth={verticalCurrentStrokeWidth}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      opacity="0.88"
                    />
                    <path
                      className="elbow-stage__vertical-current-glow"
                      d={VERTICAL_DROP_PATH}
                      fill="none"
                      stroke={verticalGlowColor}
                      strokeWidth={verticalGlowStrokeWidth}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      opacity="0.56"
                    />
                    <path
                      className="elbow-water-current elbow-water-current--primary"
                      d="M40 82 C 52 78, 64 86, 78 92 C 92 98, 106 100, 122 97 C 138 94, 152 88, 168 89 L168 109 C 154 111, 140 115, 124 115 C 102 115, 84 109, 68 102 C 56 96, 46 90, 40 90 Z"
                      fill={`url(#${currentWaterGradientId})`}
                      opacity="0.76"
                    />
                    <path
                      className="elbow-water-current elbow-water-current--secondary"
                      d="M40 88 C 54 84, 68 92, 80 97 C 94 103, 108 105, 124 102 C 140 99, 154 94, 168 95 L168 104 C 154 106, 140 110, 124 110 C 102 110, 84 104, 70 98 C 58 93, 48 89, 40 90 Z"
                      fill={`url(#${currentHighlightGradientId})`}
                      opacity="0.86"
                    />
                    <path
                      d={VERTICAL_DROP_PATH}
                      fill="none"
                      stroke={innerPipeStrokeColor}
                      strokeWidth={innerCurrentStrokeWidth}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                ) : null}

                <rect
                  className="elbow-stage__glass-sheen"
                  x={glassStripX}
                  y="20"
                  width={glassStripWidth}
                  height="98"
                  rx={glassStripWidth / 2}
                  fill={`url(#${glassGradientId})`}
                  opacity="0.56"
                />
                <rect x={centerStripX} y="18" width={centerStripWidth} height="102" fill={colorWithAlpha('var(--part-color)', 0.08)} />
                <rect x={edgeStripX} y="18" width={edgeStripWidth} height="102" fill={colorWithAlpha(mixColors(shellColor, 'var(--part-color)', 0.14), 0.08)} />
              </g>

              <path
                d={PIPE_PATH}
                fill="none"
                stroke={shellOutlineColor}
                strokeWidth={shellOutlineWidth}
                strokeLinecap="butt"
                strokeLinejoin="round"
              />

              <rect x={topCapX} y={topCapY} width={topCapWidth} height={topCapHeight} rx={topCapHeight / 2} fill={topCapColor} />
              <rect x={topCapInnerX} y={topCapInnerY} width={topCapInnerWidth} height={topCapInnerHeight} rx={topCapInnerHeight / 2} fill={topCapInner} opacity="0.86" />

              <rect x={clampRectX} y={clampRectY} width={clampRectWidth} height={clampRectHeight} rx={clampRectWidth / 2} fill={`url(#${clampGradientId})`} />
              <rect x={clampHighlightX} y={clampHighlightY} width={clampHighlightWidthPx} height={clampHighlightHeightPx} rx={clampHighlightWidthPx / 2} fill={clampHighlightColor} />
            </g>
          </svg>
        </div>
      </div>

      {!embedded && showLabel ? <span className="elbow-label">{statusLabel}</span> : null}
    </div>
  );
};

export default WaterElbow;
